import { Routes } from "@angular/router";
import { AdminComponent } from "@features/admin/admin/admin.component";
import { AuthGuard } from "@app/core/auth";
import { calGuard, roleGuard } from "@cvx/cal-angular";
import { OktaCallbackComponent } from "@okta/okta-angular";
import { RoutingComponent } from "./standalone/routing/routing.component";
import { environment } from "src/environments/environment";
import { UserRoleEnum } from "@models/user-management/user-role";

export function getOktaCallbackPath(redirectUri: string): string {
  try {
    return new URL(redirectUri).pathname.replace(/^\//, "");
  } catch {
    return redirectUri.replace(/^\//, "").split(/[?#]/)[0];
  }
}

const oktaCallbackPath = getOktaCallbackPath(environment.okta.redirectUri);

export const appRoutes: Routes = [
  {
    path: "",
    component: RoutingComponent,
  },
  {
    path: "auth",
    loadChildren: () =>
      import("@features/auth/auth.module").then((m) => m.AuthModule),
  },
  {
    path: oktaCallbackPath,
    component: OktaCallbackComponent,
  },
  {
    path: "admin",
    loadChildren: () =>
      import("@features/admin/admin.module").then((m) => m.AdminModule),
    component: AdminComponent,
    canActivate: [AuthGuard, calGuard, roleGuard],
    data: {
      module: "AdminModule",
      roles: [UserRoleEnum.Admin],
    },
  },

  {
    path: "reports",
    loadChildren: () =>
      import("@features/reports/reports.module").then((m) => m.ReportsModule),
    canActivate: [AuthGuard, calGuard, roleGuard],
    data: {
      roles: [
        UserRoleEnum.FleetOwnerSupervisor,
        UserRoleEnum.FleetOwner,
        UserRoleEnum.Admin,
        UserRoleEnum.Reader,
      ],
    },
  },
  {
    path: "equipments-utilization-report",
    loadComponent: () =>
      import("@modules/resource-management/pages/equipment-utilization-report-page/equipment-utilization-report-page.component").then(
        (c) => c.EquipmentUtilizationReportPageComponent,
      ),
    canActivate: [AuthGuard, calGuard, roleGuard],
    data: {
      roles: [
        UserRoleEnum.FleetOwnerSupervisor,
        UserRoleEnum.FleetOwner,
        UserRoleEnum.Admin,
        UserRoleEnum.Reader,
      ],
    },
  },
  {
    path: "my-requests",
    loadComponent: () =>
      import("@features/booking-requests/pages/my-booking-requests-page").then(
        (c) => c.MyBookingRequestsPageComponent,
      ),
    canActivate: [AuthGuard, calGuard, roleGuard],
    data: {
      roles: [
        UserRoleEnum.FleetOwnerSupervisor,
        UserRoleEnum.FleetOwner,
        UserRoleEnum.ServiceWorkProcessor,
        UserRoleEnum.Requestor,
        UserRoleEnum.Admin,
        UserRoleEnum.Reader,
      ],
    },
  },

  {
    path: "my-requests/create",
    loadComponent: () =>
      import("@features/booking-requests/pages/create-booking-request-page").then(
        (c) => c.CreateBookingRequestPageComponent,
      ),
    canActivate: [AuthGuard, calGuard, roleGuard],
    data: {
      roles: [
        UserRoleEnum.FleetOwnerSupervisor,
        UserRoleEnum.FleetOwner,
        UserRoleEnum.ServiceWorkProcessor,
        UserRoleEnum.Requestor,
        UserRoleEnum.Admin,
        UserRoleEnum.Reader,
      ],
    },
  },

  {
    path: "my-requests/equipment",
    loadComponent: () =>
      import("@features/booking-requests/pages/select-equipment-page").then(
        (c) => c.SelectEquipmentPageComponent,
      ),
    canActivate: [AuthGuard, calGuard, roleGuard],
    data: {
      roles: [
        UserRoleEnum.FleetOwnerSupervisor,
        UserRoleEnum.FleetOwner,
        UserRoleEnum.ServiceWorkProcessor,
        UserRoleEnum.Requestor,
        UserRoleEnum.Admin,
        UserRoleEnum.Reader,
      ],
    },
  },

  {
    path: "approvals",
    loadComponent: () =>
      import("@features/booking-requests/pages/fleet-owner-requests-page").then(
        (c) => c.FleetOwnerRequestsPageComponent,
      ),
    canActivate: [AuthGuard, calGuard, roleGuard],
    data: {
      roles: [
        UserRoleEnum.FleetOwnerSupervisor,
        UserRoleEnum.FleetOwner,
        UserRoleEnum.ServiceWorkProcessor,
        UserRoleEnum.Admin,
        UserRoleEnum.Reader,
      ],
    },
  },

  {
    path: "history",
    loadComponent: () =>
      import("@features/booking-requests/pages/booking-requests-history-page").then(
        (c) => c.BookingRequestsHistoryPageComponent,
      ),
    canActivate: [AuthGuard, calGuard, roleGuard],
    data: {
      roles: [
        UserRoleEnum.FleetOwnerSupervisor,
        UserRoleEnum.FleetOwner,
        UserRoleEnum.ServiceWorkProcessor,
        UserRoleEnum.Requestor,
        UserRoleEnum.Admin,
        UserRoleEnum.Reader,
      ],
      type: "myRequests",
    },
  },

  {
    path: "equipments",
    loadComponent: () =>
      import("@modules/resource-management/pages/equipment-page/equipment-page.component").then(
        (c) => c.EquipmentPageComponent,
      ),
    canActivate: [AuthGuard],
    data: {
      roles: [
        UserRoleEnum.FleetOwnerSupervisor,
        UserRoleEnum.FleetOwner,
        UserRoleEnum.Admin,
        UserRoleEnum.BusinessPartner,
        UserRoleEnum.Reader,
      ],
    },
  },
  {
    path: "equipments-bp-showcase",
    loadComponent: () =>
      import("@modules/resource-management/pages/equipment-bp-showcase-page/equipment-bp-showcase-page.component").then(
        (c) => c.EquipmentBpShowcasePageComponent,
      ),
    canActivate: [AuthGuard],
    data: {
      roles: [
        UserRoleEnum.FleetOwnerSupervisor,
        UserRoleEnum.FleetOwner,
        UserRoleEnum.ServiceWorkProcessor,
        UserRoleEnum.Requestor,
        UserRoleEnum.Admin,
        UserRoleEnum.BusinessPartner,
        UserRoleEnum.Reader,
      ],
    },
  },
  {
    path: "equipments/new",
    loadComponent: () =>
      import("@modules/resource-management/pages/equipment-page/components/equipment-crud/equipment-crud.component").then(
        (c) => c.EquipmentCrudComponent,
      ),
    canActivate: [AuthGuard],
    data: {
      roles: [
        UserRoleEnum.FleetOwnerSupervisor,
        UserRoleEnum.FleetOwner,
        UserRoleEnum.Admin,
        UserRoleEnum.BusinessPartner,
        UserRoleEnum.Reader,
      ],
    },
  },
  {
    path: "equipments/:id",
    loadComponent: () =>
      import("@modules/resource-management/pages/equipment-page/components/equipment-crud/equipment-crud.component").then(
        (c) => c.EquipmentCrudComponent,
      ),
    canActivate: [AuthGuard],
    data: {
      roles: [
        UserRoleEnum.FleetOwnerSupervisor,
        UserRoleEnum.FleetOwner,
        UserRoleEnum.Admin,
        UserRoleEnum.BusinessPartner,
        UserRoleEnum.Reader,
      ],
    },
  },
  {
    path: "map",
    loadComponent: () =>
      import("@features/map/pages/map-page/map-page.component").then(
        (c) => c.MapPageComponent,
      ),
    canActivate: [AuthGuard, calGuard, roleGuard],
    data: {
      roles: [
        UserRoleEnum.FleetOwnerSupervisor,
        UserRoleEnum.FleetOwner,
        UserRoleEnum.Admin,
        UserRoleEnum.Reader,
      ],
    },
  },
  {
    path: "my-guides",
    loadComponent: () =>
      import("@modules/interface-management/pages/my-guide-page/my-guide-page.component").then(
        (c) => c.MyGuidePageComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: "icons",
    loadComponent: () =>
      import("@dmms/shared/ui-kit/components/ui-icons-page/ui-icons-page.component").then(
        (c) => c.UIIconsPageComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: "access-denied",
    loadComponent: () =>
      import("@dmms/shared/ui-kit").then((c) => c.AccessDeniedComponent),
    canActivate: [AuthGuard],
  },
  {
    path: "**",
    loadComponent: () =>
      import("@dmms/shared/ui-kit").then((c) => c.PageNotFoundComponent),
    canActivate: [AuthGuard],
  },
];
